import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { focusStyles } from '@udacity/chakra-uds-theme';

import { getCatalogLink } from '~/features/catalog/utils/catalog-url-state-utils';
import { getCatalogRouteQuery } from '~/features/catalog/utils/catalog-url-state-utils';
import { isSelectOption } from '~/types/select-option';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

export function PaginationWithLinks({
  currentPage,
  totalPages,
  pageIsZeroIndexed,
  onChange,
}: {
  currentPage: number;
  totalPages: number;
  pageIsZeroIndexed?: boolean;
  onChange: (page: number) => void;
}) {
  const linkFocusStyles = focusStyles('blue.500');
  const options = Array.from({ length: totalPages }, (_, i) => ({
    label: `Page ${i + 1}`,
    value: `${i + 1}`,
  }));

  const displayedPageNumbers = useMemo(() => {
    const currentDisplayedPageNumber = currentPage + 1;

    if (totalPages === 0) {
      return [1];
    }

    if (totalPages < 5) {
      return new Array(totalPages).fill(null).map((_, index) => index + 1);
    }

    if (currentDisplayedPageNumber < 5) {
      return [1, 2, 3, 4, 5];
    }

    if (currentDisplayedPageNumber >= 5 && currentDisplayedPageNumber > totalPages - 3) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    if (currentDisplayedPageNumber >= 5 && currentDisplayedPageNumber < totalPages) {
      return [currentDisplayedPageNumber - 1, currentDisplayedPageNumber, currentDisplayedPageNumber + 1];
    }

    return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }, [currentPage, totalPages]);

  function handlePageChange(newValue: unknown) {
    if (isSelectOption(newValue)) {
      onChange(pageIsZeroIndexed ? Number(newValue.value) - 1 : Number(newValue.value));
    }
  }

  const shouldDisplayFirstPageNumber = displayedPageNumbers[0] > 1;
  const shouldDisplayLastPageNumber = !displayedPageNumbers.includes(totalPages) && totalPages > 0;

  const PagesMenu = (
    <Menu>
      <MenuButton
        aria-label='Select a page of results'
        {...(linkFocusStyles as any)}
        borderRadius='2px'
        color='black'
        height='24px'
        minWidth='24px'
        paddingX='4px'
        transition='all 0.3s ease'
        _focusVisible={{
          ...linkFocusStyles._focusVisible,

          _before: {
            ...linkFocusStyles._focusVisible._before,

            width: '100%',
            height: '100%',
          },
        }}
        _hover={{
          color: 'blue.700',
          background: 'blue.100',
        }}
      >
        ...
      </MenuButton>
      <Portal>
        <MenuList maxHeight='300px' overflow='auto'>
          {options.map((option) => (
            <MenuItem key={option.value} onClick={() => handlePageChange(option)}>
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );

  if (totalPages === 0) {
    return null;
  }

  return (
    <Flex alignItems='center' flex={1} gap='8px' justifyContent='center'>
      <IconButton
        aria-label='Show previous page of results'
        flexShrink={0}
        icon={<ChevronLeftIcon h='14px' w='14px' />}
        isDisabled={(pageIsZeroIndexed ? currentPage + 1 : currentPage) === 1}
        marginRight='8px'
        size='sm'
        variant='solid'
        onClick={() => onChange(currentPage - 1)}
      />

      {totalPages > 0 && (
        <>
          {shouldDisplayFirstPageNumber && (
            <>
              <PaginationLink pageNumber={1} />

              {PagesMenu}
            </>
          )}

          <Box display='flex' gap='8px'>
            {displayedPageNumbers.map((pageNumber) => (
              <PaginationLink key={pageNumber} pageNumber={pageNumber} />
            ))}
          </Box>

          {shouldDisplayLastPageNumber && (
            <>
              {PagesMenu}

              <PaginationLink pageNumber={totalPages} />
            </>
          )}
        </>
      )}

      <IconButton
        aria-label='Show next page of results'
        flexShrink={0}
        icon={<ChevronRightIcon h='14px' w='14px' />}
        isDisabled={(pageIsZeroIndexed && totalPages !== 0 ? currentPage + 1 : currentPage) === totalPages}
        marginLeft='8px'
        size='sm'
        variant='solid'
        onClick={() => onChange(currentPage + 1)}
      />
    </Flex>
  );
}

function PaginationLink({ pageNumber }: { pageNumber: number }) {
  const router = useRouter();
  const { parameters, ...namedQueryParams } = router.query;
  const isCurrentRoute =
    router.query?.['page'] === `page-${pageNumber}` ||
    (typeof router.query?.['page'] === 'undefined' && pageNumber === 1);
  const { query: updatedQuery } = getCatalogRouteQuery({
    parameters: { ...namedQueryParams, page: `page-${pageNumber}` },
  });
  const asPath = getCatalogLink({
    router: {
      ...router,
      query: updatedQuery,
    },
    updatedRouteParameters: {},
  });

  return (
    <Box
      key={pageNumber}
      alignItems='center'
      as={Button}
      background='transparent'
      border='solid 0.8px'
      borderColor='grey.200'
      borderRadius='8px'
      color='black !important'
      display='flex'
      fontSize='sm'
      fontWeight='500'
      h='32px'
      justifyContent='center'
      minWidth='32px'
      outline='2px solid transparent'
      p={0}
      transition='all 0.3s ease'
      variant='link'
      _focus={{
        boxShadow: 'none',
        _before: {
          width: '100%',
          height: '100%',
        },
      }}
      _hover={
        isCurrentRoute
          ? { textDecoration: 'none' }
          : { border: 'solid 0.8px', borderColor: 'blue.300', color: 'blue.500 !important', bg: 'blue.100' }
      }
      sx={
        isCurrentRoute
          ? {
              borderColor: 'blue.300',
              color: 'blue.500 !important',
              textDecoration: 'none',
              backgroundColor: 'blue.100',
            }
          : {}
      }
      onClick={() =>
        router.push({ query: updatedQuery }, asPath, {
          shallow: true,
          scroll: true,
        })
      }
    >
      {pageNumber}
    </Box>
  );
}
